import {
  calcHasFacilityViolations,
  extractConstraintLimits,
  getFacilityConstraintMetricSnapshot,
  hasNewOrWorseViolations,
  summarizeConstraintViolations,
} from './constraintViolations';
import {
  planHasAnyExposureLimit,
  parseOptionalLimit,
  resolveMaxExposureAtOutputMonth,
  resolveMaxMonthlyDrawForPlanMonth,
} from './resolvePlanCovenantLimits';
import { runCalc } from './runCalc';
import { resolveFundingCommitmentMonths } from './resolveFundingCommitment';
import { isMarketingPlanManualMode } from './resolvePlanFunding';

export function planDraw(state, fundingPct, planIndex) {
  return Math.round(
    (Number(state.marketing[planIndex]) * Number(fundingPct[planIndex] ?? 0)) / 100
  );
}

export function buildStateWithProRataRevShare(
  state,
  fundingPct,
  originalFundingPct
) {
  const revSharePct = [...(state.revSharePct ?? [])];
  for (let planIndex = 0; planIndex < fundingPct.length; planIndex += 1) {
    if (Number(fundingPct[planIndex]) !== Number(originalFundingPct[planIndex])) {
      revSharePct[planIndex] = Number(fundingPct[planIndex]) || 0;
    }
  }

  const nextState = { ...state, fundingPct, revSharePct };

  if (!isMarketingPlanManualMode(state)) {
    return nextState;
  }

  const planDrawAmount = [...(state.planDrawAmount ?? [])];
  while (planDrawAmount.length < fundingPct.length) {
    planDrawAmount.push(null);
  }
  for (let planIndex = 0; planIndex < fundingPct.length; planIndex += 1) {
    if (Number(fundingPct[planIndex]) === Number(originalFundingPct[planIndex])) {
      continue;
    }
    const marketing = Number(state.marketing[planIndex]) || 0;
    const pct = Number(fundingPct[planIndex]) || 0;
    planDrawAmount[planIndex] =
      marketing > 0 && pct > 0
        ? Math.min(Math.round((marketing * pct) / 100), marketing)
        : null;
  }

  return { ...nextState, planDrawAmount };
}

function runCalcForFundingProposal(state, fundingPct, originalFundingPct) {
  return runCalc(buildStateWithProRataRevShare(state, fundingPct, originalFundingPct));
}

function buildSyncedStateAndCalcResult(state, fundingPct, originalFundingPct) {
  const syncedState = buildStateWithProRataRevShare(
    state,
    fundingPct,
    originalFundingPct
  );
  return [syncedState, runCalc(syncedState)];
}

function isCompliant(state, fundingPct, originalFundingPct) {
  const syncedState = buildStateWithProRataRevShare(
    state,
    fundingPct,
    originalFundingPct
  );
  const calcResult = runCalc(syncedState);
  return !calcHasFacilityViolations(syncedState, calcResult);
}

function pickPlanIndexForOutputMonth(state, fundingPct, outputMonth) {
  const direct = outputMonth - 1;
  if (
    direct >= 0 &&
    direct < state.marketing.length &&
    planDraw(state, fundingPct, direct) > 0
  ) {
    return direct;
  }

  let bestPlan = -1;
  let bestDraw = 0;
  for (let planIndex = 0; planIndex < state.marketing.length; planIndex += 1) {
    if (planIndex + 1 <= outputMonth) {
      const draw = planDraw(state, fundingPct, planIndex);
      if (draw > bestDraw) {
        bestDraw = draw;
        bestPlan = planIndex;
      }
    }
  }
  return bestPlan;
}

function pickPlanIndexForEndBalViolation(state, fundingPct, calcResult, outputMonth) {
  const drawOutputMonth = outputMonth;
  const drawAtMonth = Number(calcResult.draw?.[drawOutputMonth]) || 0;
  const directPlanIndex = drawOutputMonth - 1;
  if (
    drawAtMonth > 0 &&
    directPlanIndex >= 0 &&
    directPlanIndex < state.marketing.length &&
    planDraw(state, fundingPct, directPlanIndex) > 0
  ) {
    return directPlanIndex;
  }
  return pickPlanIndexForOutputMonth(state, fundingPct, outputMonth);
}

function findPeakEndBalMonth(calcResult) {
  if (!calcResult?.endBal?.length) {
    return { peakMonth: -1, peakEndBal: 0 };
  }
  let peakMonth = 0;
  let peak = Number(calcResult.endBal[0]) || 0;
  for (let month = 1; month < calcResult.endBal.length; month += 1) {
    const value = Number(calcResult.endBal[month]) || 0;
    if (value >= peak) {
      peak = value;
      peakMonth = month;
    }
  }
  return { peakMonth, peakEndBal: peak };
}

function pickReductionTarget(state, fundingPct, calcResult, limits) {
  if (planHasAnyExposureLimit(state) && calcResult.endBal?.length) {
    let worstMonth = -1;
    let worstOver = 0;
    let worstEndBal = 0;
    for (let month = 0; month < calcResult.endBal.length; month += 1) {
      const exposureLimit = resolveMaxExposureAtOutputMonth(state, month);
      const endBal = Number(calcResult.endBal[month]) || 0;
      if (exposureLimit != null && endBal > exposureLimit) {
        const over = endBal - exposureLimit;
        if (over > worstOver) {
          worstOver = over;
          worstMonth = month;
          worstEndBal = endBal;
        }
      }
    }
    if (worstMonth >= 0) {
      const planIndex = pickPlanIndexForEndBalViolation(
        state,
        fundingPct,
        calcResult,
        worstMonth
      );
      if (planIndex >= 0 && fundingPct[planIndex] > 0) {
        return {
          planIndex,
          reason: 'Max exposure',
          peakMonth: worstMonth,
          peakEndBal: worstEndBal,
        };
      }
    }
  }

  return null;
}

function fundingPctForDraw(marketing, drawAmount) {
  if (marketing <= 0) {
    return 0;
  }
  return Math.min(100, (drawAmount * 100) / marketing);
}

function computeDrawCeiling(state, fundingPct, originalFundingPct, planIndex, limits) {
  const marketing = Number(state.marketing[planIndex]) || 0;
  if (marketing <= 0) {
    return 0;
  }

  let ceiling = planDraw(state, originalFundingPct, planIndex);

  const monthlyDrawLimit = resolveMaxMonthlyDrawForPlanMonth(state, planIndex);
  if (monthlyDrawLimit != null) {
    ceiling = Math.min(ceiling, monthlyDrawLimit);
  }

  if (planIndex > 0 && limits.maxDrawIncreasePct != null) {
    const prevDraw = planDraw(state, fundingPct, planIndex - 1);
    if (prevDraw > 0) {
      ceiling = Math.min(
        ceiling,
        Math.round(prevDraw * (1 + limits.maxDrawIncreasePct / 100))
      );
    }
  }

  return ceiling;
}

function capReasonForDrawLimit(state, fundingPct, planIndex, limits, drawAmount) {
  const monthlyDrawLimit = resolveMaxMonthlyDrawForPlanMonth(state, planIndex);
  if (monthlyDrawLimit != null && drawAmount >= monthlyDrawLimit) {
    return 'Max monthly draw';
  }
  if (planIndex > 0 && limits.maxDrawIncreasePct != null) {
    const prevDraw = planDraw(state, fundingPct, planIndex - 1);
    if (prevDraw > 0) {
      const allowedDraw = Math.round(
        prevDraw * (1 + limits.maxDrawIncreasePct / 100)
      );
      if (drawAmount >= allowedDraw) {
        return 'Max draw increase';
      }
    }
  }
  return 'Max exposure';
}

function binarySearchMaxCompliantDrawInRange(
  state,
  fundingPct,
  planIndex,
  minDraw,
  maxDraw,
  originalFundingPct,
  exposureGradeLimits
) {
  return binarySearchMaxCompliantDrawInRangeWithBase(
    state,
    fundingPct,
    planIndex,
    minDraw,
    maxDraw,
    originalFundingPct,
    exposureGradeLimits
  );
}

function binarySearchMaxCompliantDrawInRangeWithBase(
  state,
  baseFundingPct,
  planIndex,
  minDraw,
  maxDraw,
  originalFundingPct,
  exposureGradeLimits
) {
  const marketing = Number(state.marketing[planIndex]) || 0;
  if (marketing <= 0 || maxDraw <= 0) {
    return 0;
  }

  let low = Math.max(0, minDraw);
  let high = Math.max(low, maxDraw);
  let bestDraw = low;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = [...baseFundingPct];
    candidate[planIndex] = Math.min(
      Number(originalFundingPct[planIndex]) || 0,
      fundingPctForDraw(marketing, mid)
    );
    if (isCompliant(state, candidate, originalFundingPct)) {
      bestDraw = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestDraw;
}

/**
 * Like binarySearchMaxCompliantDrawInRange, but for reduceUntilCompliant's target
 * pick specifically: it only requires that reducing planIndex doesn't introduce
 * or worsen violations relative to reducing planIndex all the way to `minDraw`.
 * Some violations (e.g. exposure driven by other, later cohorts) can never be
 * resolved by reducing this one row, and the plain global compliance check would
 * otherwise force this row all the way down to `minDraw` for no benefit.
 */
function binarySearchMaxAttributableDrawInRange(
  state,
  baseFundingPct,
  planIndex,
  minDraw,
  maxDraw,
  originalFundingPct,
  exposureGradeLimits
) {
  const marketing = Number(state.marketing[planIndex]) || 0;
  if (marketing <= 0 || maxDraw <= 0) {
    return 0;
  }

  let low = Math.max(0, minDraw);
  let high = Math.max(low, maxDraw);
  let bestDraw = low;

  const floorCandidate = [...baseFundingPct];
  floorCandidate[planIndex] = Math.min(
    Number(originalFundingPct[planIndex]) || 0,
    fundingPctForDraw(marketing, low)
  );
  const baselineSnapshot = getFacilityConstraintMetricSnapshot(
    ...buildSyncedStateAndCalcResult(state, floorCandidate, originalFundingPct)
  );

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = [...baseFundingPct];
    candidate[planIndex] = Math.min(
      Number(originalFundingPct[planIndex]) || 0,
      fundingPctForDraw(marketing, mid)
    );
    const [candidateState, candidateResult] = buildSyncedStateAndCalcResult(
      state,
      candidate,
      originalFundingPct
    );
    const candidateSnapshot = getFacilityConstraintMetricSnapshot(
      candidateState,
      candidateResult
    );
    if (!hasNewOrWorseViolations(baselineSnapshot, candidateSnapshot)) {
      bestDraw = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestDraw;
}

function binarySearchMaxDrawForOutputMonthEndBal(
  state,
  baseFundingPct,
  planIndex,
  minDraw,
  maxDraw,
  originalFundingPct,
  maxExposure
) {
  const marketing = Number(state.marketing[planIndex]) || 0;
  const outputMonth = planIndex + 1;
  if (marketing <= 0 || maxDraw <= 0 || maxExposure == null) {
    return 0;
  }

  let low = Math.max(0, minDraw);
  let high = Math.max(low, maxDraw);
  let bestDraw = low;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = [...baseFundingPct];
    candidate[planIndex] = Math.min(
      Number(originalFundingPct[planIndex]) || 0,
      fundingPctForDraw(marketing, mid)
    );
    const calcResult = runCalcForFundingProposal(
      state,
      candidate,
      originalFundingPct
    );
    if ((Number(calcResult.endBal?.[outputMonth]) || 0) <= maxExposure) {
      bestDraw = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestDraw;
}

function unlockEarlierMonthsBlockedByLaterFunding(
  state,
  fundingPct,
  originalFundingPct,
  changeMap,
  exposureGradeLimits,
  limits
) {
  if (!planHasAnyExposureLimit(state)) {
    return;
  }

  let changed = true;
  let passes = 0;
  while (changed && passes < 50) {
    passes += 1;
    changed = false;

    for (let planIndex = 0; planIndex < state.marketing.length; planIndex += 1) {
      if (Number(state.marketing[planIndex]) <= 0) {
        continue;
      }

      const ceiling = computeDrawCeiling(
        state,
        fundingPct,
        originalFundingPct,
        planIndex,
        limits
      );
      const currentDraw = planDraw(state, fundingPct, planIndex);
      if (currentDraw >= ceiling) {
        continue;
      }

      const globalMaxDraw = binarySearchMaxCompliantDrawInRange(
        state,
        fundingPct,
        planIndex,
        0,
        ceiling,
        originalFundingPct,
        exposureGradeLimits
      );
      if (globalMaxDraw > currentDraw) {
        changed =
          setPlanDrawAmount(
            state,
            fundingPct,
            originalFundingPct,
            changeMap,
            planIndex,
            globalMaxDraw,
            'Max exposure'
          ) || changed;
        continue;
      }

      if (currentDraw > 0) {
        continue;
      }

      const localMaxDraw = binarySearchMaxDrawForOutputMonthEndBal(
        state,
        fundingPct,
        planIndex,
        0,
        ceiling,
        originalFundingPct,
        resolveMaxExposureAtOutputMonth(state, planIndex + 1)
      );
      if (localMaxDraw <= 0) {
        continue;
      }

      let unlocked = false;
      for (
        let trimPlanIndex = planIndex + 1;
        trimPlanIndex < state.marketing.length && !unlocked;
        trimPlanIndex += 1
      ) {
        const trimDraw = planDraw(state, fundingPct, trimPlanIndex);
        if (trimDraw <= 0) {
          continue;
        }

        let low = 0;
        let high = trimDraw - 1;
        let bestTrimDraw = trimDraw;
        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          const candidate = [...fundingPct];
          const trimMarketing = Number(state.marketing[trimPlanIndex]) || 0;
          candidate[trimPlanIndex] = Math.min(
            Number(originalFundingPct[trimPlanIndex]) || 0,
            fundingPctForDraw(trimMarketing, mid)
          );
          const unlockedDraw = binarySearchMaxCompliantDrawInRangeWithBase(
            state,
            candidate,
            planIndex,
            0,
            ceiling,
            originalFundingPct,
            exposureGradeLimits
          );
          if (unlockedDraw > currentDraw) {
            bestTrimDraw = mid;
            high = mid - 1;
          } else {
            low = mid + 1;
          }
        }

        if (bestTrimDraw >= trimDraw) {
          continue;
        }

        changed =
          setPlanDrawAmount(
            state,
            fundingPct,
            originalFundingPct,
            changeMap,
            trimPlanIndex,
            bestTrimDraw,
            'Max exposure'
          ) || changed;

        const unlockedDraw = binarySearchMaxCompliantDrawInRange(
          state,
          fundingPct,
          planIndex,
          0,
          ceiling,
          originalFundingPct,
          exposureGradeLimits
        );
        if (unlockedDraw > currentDraw) {
          changed =
            setPlanDrawAmount(
              state,
              fundingPct,
              originalFundingPct,
              changeMap,
              planIndex,
              unlockedDraw,
              'Max exposure'
            ) || changed;
          unlocked = true;
        }
      }
    }
  }
}

function fillLocalEndBalHeadroomAndRebalance(
  state,
  fundingPct,
  originalFundingPct,
  changeMap,
  exposureGradeLimits,
  limits
) {
  if (!planHasAnyExposureLimit(state)) {
    return;
  }

  for (let round = 0; round < 5; round += 1) {
    let adjusted = false;

    for (let planIndex = 0; planIndex < state.marketing.length; planIndex += 1) {
      if (Number(state.marketing[planIndex]) <= 0) {
        continue;
      }

      const ceiling = computeDrawCeiling(
        state,
        fundingPct,
        originalFundingPct,
        planIndex,
        limits
      );
      const currentDraw = planDraw(state, fundingPct, planIndex);
      if (currentDraw >= ceiling) {
        continue;
      }

      const globalMaxDraw = binarySearchMaxCompliantDrawInRange(
        state,
        fundingPct,
        planIndex,
        0,
        ceiling,
        originalFundingPct,
        exposureGradeLimits
      );
      if (globalMaxDraw > currentDraw) {
        adjusted =
          setPlanDrawAmount(
            state,
            fundingPct,
            originalFundingPct,
            changeMap,
            planIndex,
            globalMaxDraw,
            'Max exposure'
          ) || adjusted;
        continue;
      }

      const localMaxDraw = binarySearchMaxDrawForOutputMonthEndBal(
        state,
        fundingPct,
        planIndex,
        0,
        ceiling,
        originalFundingPct,
        resolveMaxExposureAtOutputMonth(state, planIndex + 1)
      );
      if (localMaxDraw <= currentDraw || localMaxDraw <= globalMaxDraw) {
        continue;
      }

      adjusted =
        setPlanDrawAmount(
          state,
          fundingPct,
          originalFundingPct,
          changeMap,
          planIndex,
          localMaxDraw,
          'Max exposure'
        ) || adjusted;
    }

    if (!adjusted) {
      break;
    }

    reduceUntilCompliant(
      state,
      fundingPct,
      originalFundingPct,
      changeMap,
      exposureGradeLimits,
      limits
    );
    restoreFundingUpToOriginalMax(
      state,
      fundingPct,
      originalFundingPct,
      changeMap,
      exposureGradeLimits,
      limits
    );
  }
}

function setPlanDrawAmount(
  state,
  fundingPct,
  originalFundingPct,
  changeMap,
  planIndex,
  drawAmount,
  reason
) {
  const marketing = Number(state.marketing[planIndex]) || 0;
  const maxPct = Number(originalFundingPct[planIndex]) || 0;
  const nextPct = Math.min(maxPct, fundingPctForDraw(marketing, drawAmount));
  const currentPct = Number(fundingPct[planIndex]) || 0;
  if (Math.abs(nextPct - currentPct) < 1e-9) {
    return false;
  }
  recordChange(
    changeMap,
    planIndex,
    originalFundingPct[planIndex],
    nextPct,
    reason
  );
  fundingPct[planIndex] = nextPct;
  return true;
}

function applyDrawLimitCaps(
  state,
  fundingPct,
  originalFundingPct,
  changeMap,
  limits
) {
  let adjusted = false;
  for (let planIndex = 0; planIndex < state.marketing.length; planIndex += 1) {
    if (Number(state.marketing[planIndex]) <= 0) {
      continue;
    }
    const ceiling = computeDrawCeiling(
      state,
      fundingPct,
      originalFundingPct,
      planIndex,
      limits
    );
    const currentDraw = planDraw(state, fundingPct, planIndex);
    if (currentDraw > ceiling) {
      adjusted =
        setPlanDrawAmount(
          state,
          fundingPct,
          originalFundingPct,
          changeMap,
          planIndex,
          ceiling,
          capReasonForDrawLimit(
            state,
            fundingPct,
            planIndex,
            limits,
            ceiling
          )
        ) || adjusted;
    }
  }
  return adjusted;
}

function reduceUntilCompliant(
  state,
  fundingPct,
  originalFundingPct,
  changeMap,
  exposureGradeLimits,
  limits
) {
  let rounds = 0;
  while (
    !isCompliant(state, fundingPct, originalFundingPct) &&
    rounds < 100
  ) {
    rounds += 1;
    const capped = applyDrawLimitCaps(
      state,
      fundingPct,
      originalFundingPct,
      changeMap,
      limits
    );
    if (isCompliant(state, fundingPct, originalFundingPct)) {
      break;
    }

    const calcResult = runCalcForFundingProposal(
      state,
      fundingPct,
      originalFundingPct
    );
    const target = pickReductionTarget(state, fundingPct, calcResult, limits);
    if (!target) {
      break;
    }

    const planIndex = target.planIndex;
    const ceiling = computeDrawCeiling(
      state,
      fundingPct,
      originalFundingPct,
      planIndex,
      limits
    );
    const currentDraw = planDraw(state, fundingPct, planIndex);
    const compliantDraw = binarySearchMaxAttributableDrawInRange(
      state,
      fundingPct,
      planIndex,
      0,
      ceiling,
      originalFundingPct,
      exposureGradeLimits
    );
    if (compliantDraw === currentDraw) {
      if (!capped) {
        break;
      }
      continue;
    }

    setPlanDrawAmount(
      state,
      fundingPct,
      originalFundingPct,
      changeMap,
      planIndex,
      compliantDraw,
      target.reason
    );
  }
}

function restoreFundingUpToOriginalMax(
  state,
  fundingPct,
  originalFundingPct,
  changeMap,
  exposureGradeLimits,
  limits
) {
  let restored = true;
  while (restored) {
    restored = false;
    for (let planIndex = 0; planIndex < state.marketing.length; planIndex += 1) {
      if (Number(state.marketing[planIndex]) <= 0) {
        continue;
      }
      const ceiling = computeDrawCeiling(
        state,
        fundingPct,
        originalFundingPct,
        planIndex,
        limits
      );
      const currentDraw = planDraw(state, fundingPct, planIndex);
      if (currentDraw >= ceiling) {
        continue;
      }
      const maxCompliantDraw = binarySearchMaxCompliantDrawInRange(
        state,
        fundingPct,
        planIndex,
        0,
        ceiling,
        originalFundingPct,
        exposureGradeLimits
      );
      if (maxCompliantDraw > currentDraw) {
        restored =
          setPlanDrawAmount(
            state,
            fundingPct,
            originalFundingPct,
            changeMap,
            planIndex,
            maxCompliantDraw,
            'Max exposure'
          ) || restored;
      }
    }
  }
}

function recordChange(changeMap, planIndex, fromPct, toPct, reason) {
  const existing = changeMap.get(planIndex) ?? {
    planIndex,
    fromPct,
    toPct,
    reasons: [],
  };
  existing.toPct = toPct;
  if (!existing.reasons.includes(reason)) {
    existing.reasons.push(reason);
  }
  changeMap.set(planIndex, existing);
}

function buildChanges(
  state,
  originalFundingPct,
  proposedFundingPct,
  proposedRevSharePct,
  changeMap,
  planMonthLabels
) {
  return [...changeMap.values()]
    .filter((entry) => entry.toPct !== entry.fromPct)
    .sort((a, b) => a.planIndex - b.planIndex)
    .map((entry) => {
      const marketing = Number(state.marketing[entry.planIndex]) || 0;
      const planIndex = entry.planIndex;
      return {
        planIndex,
        planMonthLabel: planMonthLabels?.[planIndex] ?? `Month ${planIndex + 1}`,
        marketing,
        fromPct: entry.fromPct,
        toPct: entry.toPct,
        fromRevSharePct: Number(state.revSharePct?.[planIndex]) || 0,
        toRevSharePct: Number(proposedRevSharePct[planIndex]) || 0,
        fromDraw: planDraw(state, originalFundingPct, planIndex),
        toDraw: planDraw(state, proposedFundingPct, planIndex),
        reasons: entry.reasons,
      };
    });
}

function proposeManualModeFundingFixes(state, exposureGradeLimits, planMonthLabels) {
  const originalFundingPct = state.fundingPct.map((value) => Number(value) || 0);
  const fundingPct = [...originalFundingPct];
  const changeMap = new Map();

  const baselineCalc = runCalc(state);
  const violatedConstraints = summarizeConstraintViolations(
    state,
    baselineCalc,
    exposureGradeLimits
  ).summaries;

  for (let planIndex = 0; planIndex < state.marketing.length; planIndex += 1) {
    const rowLimit = parseOptionalLimit(state?.planMaxExposure?.[planIndex]);
    if (rowLimit == null) {
      continue;
    }
    const marketing = Number(state.marketing[planIndex]) || 0;
    if (marketing <= 0) {
      continue;
    }
    const ceiling = planDraw(state, originalFundingPct, planIndex);
    const currentDraw = planDraw(state, fundingPct, planIndex);
    const maxDraw = binarySearchMaxDrawForOutputMonthEndBal(
      state,
      fundingPct,
      planIndex,
      0,
      ceiling,
      originalFundingPct,
      rowLimit
    );
    if (maxDraw < currentDraw) {
      setPlanDrawAmount(
        state,
        fundingPct,
        originalFundingPct,
        changeMap,
        planIndex,
        maxDraw,
        'Max exposure'
      );
    }
  }

  const commitmentMonths = resolveFundingCommitmentMonths(state);
  for (let i = commitmentMonths; i < fundingPct.length; i += 1) {
    fundingPct[i] = 0;
  }

  const proposedState = buildStateWithProRataRevShare(
    state,
    fundingPct,
    originalFundingPct
  );
  const feasible = isCompliant(state, fundingPct, originalFundingPct);
  const changes = buildChanges(
    state,
    originalFundingPct,
    fundingPct,
    proposedState.revSharePct,
    changeMap,
    planMonthLabels
  );

  const finalCalcResult = runCalc(proposedState);
  const peakInfo = findPeakEndBalMonth(finalCalcResult);

  return {
    changes,
    proposedFundingPct: fundingPct,
    proposedRevSharePct: proposedState.revSharePct,
    violatedConstraints,
    feasible,
    projectedPeakMaxExposure: peakInfo.peakEndBal,
    projectedPeakMonth: peakInfo.peakMonth >= 0 ? finalCalcResult.cols?.[peakInfo.peakMonth] : null,
  };
}

export function proposeFundingFixes(state, exposureGradeLimits, planMonthLabels = []) {
  if (isMarketingPlanManualMode(state)) {
    return proposeManualModeFundingFixes(state, exposureGradeLimits, planMonthLabels);
  }

  const limits = extractConstraintLimits(state, exposureGradeLimits);
  const originalFundingPct = state.fundingPct.map((value) => Number(value) || 0);
  const fundingPct = [...originalFundingPct];
  const changeMap = new Map();

  const baselineCalc = runCalc(state);
  const violatedConstraints = summarizeConstraintViolations(
    state,
    baselineCalc,
    exposureGradeLimits
  ).summaries;

  reduceUntilCompliant(
    state,
    fundingPct,
    originalFundingPct,
    changeMap,
    exposureGradeLimits,
    limits
  );

  restoreFundingUpToOriginalMax(
    state,
    fundingPct,
    originalFundingPct,
    changeMap,
    exposureGradeLimits,
    limits
  );

  unlockEarlierMonthsBlockedByLaterFunding(
    state,
    fundingPct,
    originalFundingPct,
    changeMap,
    exposureGradeLimits,
    limits
  );

  fillLocalEndBalHeadroomAndRebalance(
    state,
    fundingPct,
    originalFundingPct,
    changeMap,
    exposureGradeLimits,
    limits
  );

  const commitmentMonths = resolveFundingCommitmentMonths(state);
  for (let i = commitmentMonths; i < fundingPct.length; i += 1) {
    fundingPct[i] = 0;
  }

  const proposedState = buildStateWithProRataRevShare(
    state,
    fundingPct,
    originalFundingPct
  );
  const feasible = isCompliant(state, fundingPct, originalFundingPct);
  const changes = buildChanges(
    state,
    originalFundingPct,
    fundingPct,
    proposedState.revSharePct,
    changeMap,
    planMonthLabels
  );

  const finalCalcResult = runCalc(proposedState);
  const peakInfo = findPeakEndBalMonth(finalCalcResult);

  return {
    changes,
    proposedFundingPct: fundingPct,
    proposedRevSharePct: proposedState.revSharePct,
    violatedConstraints,
    feasible,
    projectedPeakMaxExposure: peakInfo.peakEndBal,
    projectedPeakMonth: peakInfo.peakMonth >= 0 ? finalCalcResult.cols?.[peakInfo.peakMonth] : null,
  };
}
